import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBan,
    faUnlock,
    faInfoCircle,
    faSpinner,
    faSearch,
    faFilter,
    faCopy,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import adminSecurityService from '../../../helpers/adminSecurityService';
import IPStatsModal from './IPStatsModal';

const BlockedIPsList = () => {
    const [loading, setLoading] = useState(true);
    const [blockedIPs, setBlockedIPs] = useState([]);
    const [filteredIPs, setFilteredIPs] = useState([]);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'temporary', 'permanent'
    const [selectedIP, setSelectedIP] = useState(null);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [copiedIP, setCopiedIP] = useState(null);

    useEffect(() => {
        fetchBlockedIPs();
    }, []);

    useEffect(() => {
        filterIPs();
    }, [searchTerm, filterType, blockedIPs]);

    const fetchBlockedIPs = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminSecurityService.getBlockedIPs();
            setBlockedIPs(data || []);
            setFilteredIPs(data || []);
        } catch (err) {
            console.error('Error fetching blocked IPs:', err);
            setError(err.message || 'Error al cargar IPs bloqueadas');
        } finally {
            setLoading(false);
        }
    };

    const filterIPs = () => {
        let filtered = [...blockedIPs];

        // Filtrar por tipo
        if (filterType === 'temporary') {
            filtered = filtered.filter((ip) => !ip.isPermanent);
        } else if (filterType === 'permanent') {
            filtered = filtered.filter((ip) => ip.isPermanent);
        }

        // Filtrar por búsqueda
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (ip) =>
                    ip.ip.toLowerCase().includes(term) ||
                    ip.reason?.toLowerCase().includes(term)
            );
        }

        setFilteredIPs(filtered);
    };

    const handleUnblock = async (ip) => {
        if (
            !window.confirm(
                `¿Estás seguro de que deseas desbloquear la IP ${ip}?`
            )
        ) {
            return;
        }

        try {
            await adminSecurityService.unblockIP(ip);
            alert('IP desbloqueada exitosamente');
            fetchBlockedIPs();
        } catch (err) {
            console.error('Error unblocking IP:', err);
            alert('Error al desbloquear IP: ' + err.message);
        }
    };

    const handleViewStats = async (ip) => {
        setSelectedIP(ip);
        setShowStatsModal(true);
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedIP(text);
            setTimeout(() => setCopiedIP(null), 2000);
        } catch (err) {
            console.error('Error copying to clipboard:', err);
        }
    };

    const isExpired = (ipData) => {
        if (ipData.isPermanent) return false;
        if (!ipData.blockedUntil) return false;
        return new Date(ipData.blockedUntil) < new Date();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-gray-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faBan} className="text-red-600 mt-0.5" />
                    <div>
                        <h5 className="font-semibold text-red-900 mb-1">Error al cargar IPs bloqueadas</h5>
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            placeholder="Buscar por IP o razón..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                    </div>

                    {/* Filter by type */}
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        >
                            <option value="all">Todas</option>
                            <option value="temporary">Temporales</option>
                            <option value="permanent">Permanentes</option>
                        </select>
                    </div>
                </div>

                <div className="text-sm text-gray-600">
                    Mostrando {filteredIPs.length} de {blockedIPs.length} IPs bloqueadas
                </div>
            </div>

            {/* Table */}
            {filteredIPs.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <FontAwesomeIcon icon={faBan} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-600">No se encontraron IPs bloqueadas</p>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        IP
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Razón
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Intentos
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Bloqueado hasta
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredIPs.map((ipData) => {
                                    const expired = isExpired(ipData);
                                    return (
                                        <tr
                                            key={ipData._id || ipData.ip}
                                            className={`hover:bg-gray-50 ${expired ? 'opacity-60' : ''}`}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-mono text-gray-900">{ipData.ip}</span>
                                                    <button
                                                        onClick={() => copyToClipboard(ipData.ip)}
                                                        className="text-gray-400 hover:text-gray-600"
                                                        title="Copiar IP"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={copiedIP === ipData.ip ? faCheck : faCopy}
                                                            className={copiedIP === ipData.ip ? 'text-green-600' : ''}
                                                        />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                                {ipData.reason || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {ipData.isPermanent ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        Permanente
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        Temporal
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {ipData.blockedAttempts || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {ipData.isPermanent ? (
                                                    <span className="text-gray-400">Indefinido</span>
                                                ) : ipData.blockedUntil ? (
                                                    new Date(ipData.blockedUntil).toLocaleString('es-ES')
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {expired ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                        Expirado
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Activo
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleViewStats(ipData.ip)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Ver estadísticas"
                                                    >
                                                        <FontAwesomeIcon icon={faInfoCircle} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleUnblock(ipData.ip)}
                                                        className="text-green-600 hover:text-green-900"
                                                        title="Desbloquear IP"
                                                    >
                                                        <FontAwesomeIcon icon={faUnlock} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Stats Modal */}
            {showStatsModal && selectedIP && (
                <IPStatsModal
                    ip={selectedIP}
                    onClose={() => {
                        setShowStatsModal(false);
                        setSelectedIP(null);
                    }}
                />
            )}
        </div>
    );
};

export default BlockedIPsList;

